import maplibregl, * as M from 'maplibre-gl';
import * as turf from '@turf/turf';
import circle from '@turf/circle';

import {
   MapStyle,
   ViewerState
} from '../common/managers/Types';
import { MitLatLng, GenerateGeomInterface, MitLayerHandle, MapFacadeAbstract, CircleSelectorAbstract, PriotisedMapLayerTypes } from './MapFacade';
import { GenerateGeom } from './GenerateGeom';
import { SettingsManager } from '@viamap/viamap2-common';
import { FeatureLayer } from './WmsLayerFunc';
import { createDonutChart } from './PieChartDonut';
import { MapImageCache } from './MapImageCache';
import { VectorLayer } from './VectorLayerFunc';
import { Spider } from './Spider';
import { BuiltInVectorSources, getMapStyle, getVectorSource } from './LinkToMapStyle';
import { GeojsonWFS } from './WFSGeojsonLayerFunc';
import { WFStoGeoJson } from 'src/functions/WFSutils';
import { DynamicDataDrivenLayer, DynamicDataDrivenLayerPropertyInfo } from './DynamicDataDrivenLayer';
import { GlyphProtocolHandler, stylerSetGlyphProtocol } from './MapFontManager';

// export type MitEvent = M.MapboxEvent;

enum SelectionState { Disabled, ClickToSelectCenter, CenterSelected, RimSelected, WaitingForServer }



export class MitLayerHandleMaplibre implements MitLayerHandle {
   source: any;
   sourceId: string = "";
   layerList: any[] = [];
   // layersConsumingEvents:string[]=[];
   layersWithPieCharts: string[] = [];

   static getUniqueId() {
      // todo: implement
   }

   constructor() {
      // super();
   }

   hasItems(): boolean {
      // todo implement;
      return false;
   }

   clearLayers(map: MapFacadeAbstract) {
      map?.removeLayer?.(this);

      this.source = undefined;
      this.layerList = [];
      this.layersWithPieCharts = [];
      this.sourceId = "";
   }

   updateSource(map: MapFacadeMaplibre, source: any) {
      this.source.data = source;
      (map.getMapPop().getSource(this.sourceId) as any)?.setData?.(this.source.data)
   }

   updateLayerFromHandle(map: MapFacadeMaplibre, layerHandler: MitLayerHandleMaplibre) {
      let nextLayers = layerHandler.getLayers()
      nextLayers.forEach((a) => a.source = this.sourceId)
      layerHandler.addSource(this.sourceId, {})
      map.addLayer(layerHandler, [this])
      this.sourceId = ""
      map.removeLayer(this)
      this.layerList = nextLayers
      this.sourceId = layerHandler.sourceId
   }

   addSource(id: string, source: any) {
      this.source = source; this.sourceId = id;
   }
   hasSource() {
      return Boolean(this.source && this.sourceId);
   }


   getSourceId() {
      return this.sourceId;
   }
   getSource() {
      return this.source;
   }

   addLayer(layer: any) {
      this.layerList.push(layer);
   }
   hasLayers() {
      return Boolean(this.layerList && this.layerList.length > 0);
   }
   getLayers() {
      return this.layerList;
   }
   getLayerIds() {
      // tslint:disable-next-line
      return this.layerList.map((layer) => { return layer['id']; });
   }
   remove() {
      // todo: implement
   }

   addLayerConsumingEvents(_id: string) {
      // this.layersConsumingEvents.push(id); 
   }
   getLayersConsumingEvents(): string[] {
      // return this.layersConsumingEvents;
      return []
   }

   moveLayer(map: MapFacadeAbstract) {
      map.moveLayer(this);
   }

   addTo(map: MapFacadeAbstract)
   addTo(map: MapFacadeAbstract, before: MitLayerHandle[])
   addTo(map: MapFacadeAbstract, before: MitLayerHandle[] | undefined)
   addTo(map: MapFacadeAbstract, before?: MitLayerHandle[]) {
      if (before?.length) {
         map.addLayer(this, before);
      } else {
         map.addLayer(this)
      }
   }

   addlayersWithPieCharts(id: string) {
      this.layersWithPieCharts.push(id);
   }

}

type mapFacadeOptions = {
   mapStyle?: MapStyle
}
export class MapFacadeMaplibre implements MapFacadeAbstract {

   public readonly ControlPosition = {
      TopRight: "top-right",
      TopLeft: "top-left",
      BottomRight: "bottom-right",
      BottomLeft: "bottom-left"
   };

   private banList: any[] = [];
   private urlToLabels: { urlIdent: string, label: string, x: number, y: number }[] = [];
   private map: M.Map;
   private loadingMoveEnd;
   private mapLayoutRestore: { [key: string]: "visible" | "none" | undefined } = {};
   private _isInitialized = false;
   private generateGeomObj: GenerateGeomInterface | undefined;
   private markersBySourceId: { [sourceId: string]: { [id: number]: M.Marker } } = {};
   private layerControl: undefined;
   private catchment: MitLayerHandleMaplibre | undefined;
   private attributionList: string[] = [];
   private rotationCallbackHandler: ((rotation: number) => void) | undefined;
   private moveendCallbackHandler: ((viewState: ViewerState) => void) | undefined;
   private moveendFeatureLayer: { sourceID: string, minZoom: number, dataGetter: (bounds) => any, dataTransformer: (data, options: { zoom: any, bounds: any }) => any }[] = [];

   // store the map configuration properties in an object,
   // we could also move this to a separate file & import it if desired.
   // private config:L.MapOptions = { 
   // //                    center: [55.486051, 10.513315], // Odense Fjord
   // //                    zoom: 10, // start zoom
   //                     zoomControl: false,
   //                     center: [56.304411, 10.508832], // Ved Rønde på Djursland
   //                     zoom: 8, // start zoom
   //                     maxZoom: Utils.getSystemSetting("maxZoom", 18),
   //                     minZoom: Utils.getSystemSetting("minZoom", 3),
   // //                    scrollwheel: false,
   // //                    legends: true,
   // //                    infoControl: false,
   //                     attributionControl: false,
   //                     renderer: L.canvas()
   //                     //  preferCanvas: true, Note canvas performs better but IE is not supported
   //                 };

   constructor(containerElement: any, _isOrthophotoGlobal?: boolean, options?: (Partial<M.MapOptions> & mapFacadeOptions)) {

      let defaultBounds = SettingsManager.getSystemSetting("MapInitializationBounds", [[7.741269070919969, 57.808475243053806], [13.511745485770007, 54.47934487366999]]);

      let tileTimeoutSeconds = SettingsManager.getSystemSetting("tileTimeoutSeconds", 5);
      let tileTimeout = tileTimeoutSeconds * 1000; // convert to milliseconds

      let style = this.mapStyleStyle(options?.mapStyle?.style, options?.mapStyle?.mods ?? [])
      let source = getVectorSource(options?.mapStyle?.source);

      var map2 = new M.Map({
         container: containerElement,
         hash: true,
         style: { ...style, sources: source } as any,
         trackResize: true,
         validateStyle: true,
         bounds: defaultBounds,
         transformRequest: (url, resourceType) => {
            if (resourceType === 'Tile' && url.startsWith('http://13.48.91.12:8080/geoserver')) {
               return {
                  url: url,
                  headers: { 'Origin': '*' }
               };
            } else {
               return {
                  url: url,
               };
            }
         }
         , ...options
      })


      // document.getElementById("Mit-MapOverlay").append(control.onAdd(map2))


      map2.on('load', () => {
         this._isInitialized = true;
         const allMapLayers = map2.getStyle().layers
         const viavectorLayers = allMapLayers.filter((a) => (a && ["viavector", "hillshade", "orthophoto"].includes(a["source"])));
         viavectorLayers.forEach((a) => {
            this.mapLayoutRestore[a.id] = a.layout?.visibility
         })
      })
      this.initEventManagers(map2);
      map2.addControl(new M.NavigationControl({}), SettingsManager.getSystemSetting("NavigationControlLocation"));
      map2.addControl(new M.ScaleControl({}), 'bottom-left');
      // ----------------------------------- load icons ----------------------------------
      let scale = 1;
      map2.loadImage(`https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png`).then((image) => {
         map2.addImage('blue-pin', image.data as any, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)

      })

      map2.loadImage(`https://cdn.rawgit.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png`).then((image) => {
         map2.addImage('red-pin', image.data as any, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)
      })


      let legImg = Spider.generateLegImage(
         [0, 0],
         [100, 100],
         2,
         "black",
      );

      map2.addImage('spider-line', legImg, { pixelRatio: scale }); // 38x55px, shadow adds 5px (for scale eq 1)
      maplibregl.addProtocol("glyphs", GlyphProtocolHandler);
      maplibregl.addProtocol("proxy", async (params, abortController): Promise<M.GetResourceResponse<{}>> => {
         const corsProxy = SettingsManager.getSystemSetting("ImageCorsProxyUrl")
         let urlIdent = urlIdentFromUrl(params.url)
         if (this.banList.includes(urlIdent)) {
            return { data: await generateTileDataWithText() }
         }

         const resp = fetch(corsProxy + `https://${params.url.split("://")[1]}`, {
            headers: { "Accept": "image/png" },
            signal: abortController.signal
         })
         let x = setTimeout(() => {
            if (abortController.signal.aborted) return;
            abortController.abort("Timeout")
            this.banList.push(urlIdent)
         }, tileTimeout)
         try {
            const awaited = await resp
            clearTimeout(x)

            if (awaited.status == 200) {
               if (awaited.headers.get("Content-Type")?.includes("text")) {
                  this.banList.push(urlIdent)
                  return { data: await generateTileDataWithText() }
               }
               return { data: await awaited.arrayBuffer() }
            } else {
               return { data: await generateTileDataWithText("Error: " + awaited.statusText) }
               // throw new Error(`Tile fetch error: ${awaited.statusText}`);
            }
         } catch (error) {
            console.error("Timeout error: " + error)
            return { data: await generateTileDataWithText() }
         }
      });

      maplibregl.addProtocol("normal", async (params, abortController: AbortController): Promise<M.GetResourceResponse<{}>> => {
         let urlIdent = urlIdentFromUrl(params.url)
         if (params.url.includes("glyph")) {
            console.log("Glyph url: " + params.url);
         }
         if (this.banList.includes(urlIdent)) {
            let info = this.readLabelFromUrl(urlIdent)
            return { data: await generateTileDataWithText(info?.label, info?.x, info?.y) }
         }

         const resp = fetch(`https://${params.url.split("://")[1]}`, {
            headers: { "Accept": "*/*" },
            signal: abortController.signal
         })
         let x = setTimeout(() => {
            if (abortController.signal.aborted) return;
            abortController.abort("Timeout")
            this.banList.push(urlIdent)
         }, tileTimeout)
         try {
            const awaited = await resp
            clearTimeout(x)
            if (awaited.status == 200) {
               if (awaited.headers.get("Content-Type")?.includes("text")
                  || awaited.headers.get("Content-Type")?.includes("html")
                  || awaited.headers.get("Content-Type")?.includes("json")
                  || awaited.headers.get("Content-Type")?.includes("xml")
               ) {
                  // this.banList.push(urlIdent)   *
                  let info = this.readLabelFromUrl(urlIdent)
                  return { data: await generateTileDataWithText(info?.label, info?.x, info?.y) }
               }
               return { data: await awaited.arrayBuffer() }
            } else {
               let info = this.readLabelFromUrl(urlIdent)
               return { data: await generateTileDataWithText(info?.label, info?.x, info?.y) }
               // throw new Error(`Tile fetch error: ${awaited.statusText}`);
            }
         } catch (error) {
            console.error("Timeout error: " + error)
            let info = this.readLabelFromUrl(urlIdent)
            return { data: await generateTileDataWithText(info?.label, info?.x, info?.y) }
         }
      });

      this.banList = []
      this.map = map2;
   }

   /**
    * Registers a callback handler for when a rotation event occurs.
    * @param rotationHandler Function to occur on rotation event.
    */
   public registerRotationCallbackHandler(rotationHandler: (rotation: number) => void) {

      this.rotationCallbackHandler = rotationHandler;

   }

   public setUrlToLabels(a: typeof this.urlToLabels) {
      this.urlToLabels = a
   }

   public readLabelFromUrl(url: string) {
      let label = this.urlToLabels.find((a) => url.includes(a.urlIdent))
      return label
   }

   public restoreNormalVisibility() {
      let layer = Object.keys(this.mapLayoutRestore)
      layer.forEach((a) => {
         this.map.setLayoutProperty(a, 'visibility', this.mapLayoutRestore[a] || "visible")
      })
   }

   public updateMarkers(sourceId: string) {
      const features = this.map.querySourceFeatures(sourceId);

      if (!this.markersBySourceId[sourceId]) {
         return //should do nothing
      } else {
         // remove all old markers
         Object.values(this.markersBySourceId[sourceId]).forEach((m) => {
            m.remove();
         });
      }

      // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
      // and add it to the map if it's not there already
      for (let i = 0; i < features.length; i++) {
         const coords = (features[i].geometry as any).coordinates;
         const props = features[i].properties;
         if (!props.cluster) continue;
         const id = props.cluster_id;

         let marker = this.markersBySourceId[sourceId] && this.markersBySourceId[sourceId][id];
         if (marker) {
            // remove old marker
            marker.remove();
         }
         // create marker
         const el = createDonutChart(props);
         marker = new maplibregl.Marker({
            element: el as any
         }).setLngLat(coords);
         marker.addTo(this.map);
         this.markersBySourceId[sourceId][id] = marker;
      }
   }

   /**
    * Registers a callback handler for when a moveend event occurs.
    * @param moveendHandler Function to occur on moveend event.
    * sends a state
    * {
         center:[number, number],
         pitch:number,
         zoom:number,
         bearing:number
      }
    */
   public registerMoveendCallbackHandler(moveendHandler: (viewerState: ViewerState) => void) {
      this.moveendCallbackHandler = moveendHandler;
   }

   public customLoading() {
      return this.loadingMoveEnd;
   }

   public initEventManagers(map: M.Map) {
      map.on('styleimagemissing', (e: any) => {
         if (!e.id || !e.type) {
            return;
         }
         let id = e.id; // id of the missing image
         // TODO: Move following id.startsWith to ImageGeneration Manager
         if (id.startsWith("mit-pin")) {
            const color = id.split("mit-pin-")[1]
            GenerateGeom.GetEveryMitPin(color, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("NS-pin")) {
            const color = id.split("NS-pin-")[1]
            GenerateGeom.GetEveryNSPin(color, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("mit-poi-")) {
            const icon = id.split("mit-poi-")[1]
            GenerateGeom.GetEveryMitPOI(icon, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("NS-poi-")) {
            const icon = id.split("NS-poi-")[1]
            GenerateGeom.GetEveryNSPOI(icon, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         if (id.startsWith("mit-patt-")) {
            GenerateGeom.getEveryMitPattern(id, (url) => {
               map.loadImage(url).catch((error) => console.error("Got error loading image: " + id + " :" + error.message))
                  .then((image) => {
                     if (map.hasImage(id))
                        return
                     if (image) map.addImage(id, image.data, { pixelRatio: 2 })
                  })
            })
            return
         }
         MapImageCache.loadImage(id, (image) => {
            if (map.hasImage(id))
               return
            map.addImage(id, image);
         })
      });
      map.on('error', (ev) => {
         if ("sourceId" in ev && typeof ev.sourceId == "string" && ev.sourceId.includes("backgroundlayer")) {
            if ("source" in ev) {
               let message = ErrorMessageMapfacade(ev.source as M.Source)
               document.getElementById("NonBlockingError")?.dispatchEvent(new CustomEvent("NonBlockingError", { "detail": message }))
            }
         }

         console.error("Maplibre error: " + ev.error.message)
      })
      map.on('rotate', () => {
         this.rotationCallbackHandler?.(-map.getBearing());
      });

      map.on('moveend', () => {
         let cen = new MitLatLng(map.getCenter().lat, map.getCenter().lng)
         const state = {
            bearing: map.getBearing(),
            pitch: map.getPitch(),
            zoom: map.getZoom(),
            center: cen
         };
         this.moveendCallbackHandler?.(state);
      });
      map.once('idle', () => {
         let lHandle = new MitLayerHandleMaplibre()
         lHandle.addSource("idTest", { type: "geojson", data: turf.featureCollection([]) });
         lHandle.addLayer({
            id: "testtt",
            type: 'line',
            source: "idTest",
            paint: {
               "line-color": ["match",
                  ["get", "anvendelse"],
                  "S", "red",
                  "VV", "blue",

                  "green"
               ],
               'line-width': 3,
            }
         })
         lHandle.addTo(this);
      })

      map.on('moveend', async (e) => {
         this.loadingMoveEnd = true;
         let bounds = e.target.getBounds();
         let zoom = e.target.getZoom();
         let end = this.moveendFeatureLayer.map(async (a) => {
            if (a.minZoom > zoom) {
               return
            }
            a.dataGetter(bounds).then(async (data) => {
               (this.map.getSource(a.sourceID) as M.GeoJSONSource | undefined)?.setData(
                  await a.dataTransformer?.(data, { zoom: zoom, bounds: bounds }) || data
               )
            })
         })
         await Promise.all(end);
         this.loadingMoveEnd = false;
      })
      // ----------------- PIE MARKERS -----------------



      // ----------------- PIE MARKERS -----------------


      // code for creating an SVG donut chart from feature properties



      // ----------------- PIE MARKERS -----------------
      // after the GeoJSON data is loaded, update markers on the screen and do so on every map move/moveend
      map.on('data', (e: any) => {
         if (!e.sourceId || !(e.sourceId as string).startsWith("layer-source-pie") || !e.isSourceLoaded) {
            return;
         }
         // update this specific layer from source
         this.updateMarkers(e.sourceId as string);

         const updateAllPieMarkers = () => {
            Object.keys(this.markersBySourceId).forEach((sourceId) => {
               this.updateMarkers(sourceId);
            })
         }
         map.on('move', () => {
            updateAllPieMarkers()
         });
         map.on('moveend', () => {
            updateAllPieMarkers()
         });
      });

      // ----------------- PIE MARKERS END -----------------

   }

   public setMinZoom(minZoom?: number | null | undefined) {
      return this.map?.setMinZoom?.(minZoom)
   }

   public setMaxZoom(maxZoom?: number | null | undefined) {
      return this.map?.setMaxZoom?.(maxZoom)
   }

   public isInitialized(): boolean {
      return Boolean(this._isInitialized);
   }

   public setFeatureState(feature, state) {
      this.map.setFeatureState(feature, state)
   }

   removeSource(source: string) {
      this.map.removeSource(source)
   }

   // public getGenerateGeom() {
   //    return this.generateGeomObj || (this.generateGeomObj = new GenerateGeom(), this.generateGeomObj);
   // }

   public getCircleSelector() {
      return null!;
   }

   public addZoomControl() {
      this.map?.addControl(
         new M.NavigationControl(
            {
               'showCompass': true,
               'showZoom': true
            }
         ),
         'top-left'
      );
   }

   public getZoomControl(): string {
      // let c = new DOMParser().parseFromString(
      //    `
      // <div class="mapboxgl-ctrl mapboxgl-ctrl-group">
      //    <button class="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in" type="button"
      //       title="Zoom in" aria-label="Zoom in">
      //    </button>
      //    <button class="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
      //       type="button" title="Zoom out" aria-label="Zoom out">
      //    </button>
      //    <button
      //       class="mapboxgl-ctrl-icon mapboxgl-ctrl-compass" type="button" title="Reset bearing to north"
      //       aria-label="Reset bearing to north">
      //       <span class="mapboxgl-ctrl-compass-arrow"
      //          style="transform: rotate(0deg);">
      //       </span>
      //    </button>
      // </div>
      //    `, 
      //    'text/xml');
      // return c.body;
      return `
      <div id="mit-container-top-left" className="mit-control-container">
      <div className="mapboxgl-ctrl mapboxgl-ctrl-group">
<button className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in" type="button"
  title="Zoom in" aria-label="Zoom in">
</button>
<button className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
  type="button" title="Zoom out" aria-label="Zoom out">
</button>
<button
  className="mapboxgl-ctrl-icon mapboxgl-ctrl-compass" type="button" title="Reset bearing to north"
  aria-label="Reset bearing to north">
  <span className="mapboxgl-ctrl-compass-arrow">
  </span>
</button>
</div>
      `;
   }

   public addScaleControl() {
      // this.map && this.map.addControl(new M.ScscaleControl({}), 'bottom-left');
   }

   /**
    * @deprecated use switchMapStyle
    */
   public setBackgroundLayer(layer?: string) {
      if (layer === undefined || layer === null || layer === "") {
         return;
      }
      if (this.map) {
         // Switch on Areal Image
         switch (layer) {
            case 'Orthophoto': {
               this.restoreNormalVisibility()
               this.map.setLayoutProperty('orthophoto', 'visibility', 'visible');
               this.map.setLayoutProperty('building', 'visibility', 'none');
               this.map.setLayoutProperty('building-3d', 'visibility', 'none');
               this.map.setLayoutProperty('water_ocean_lowzoomonly', 'visibility', 'visible');
               this.map.setLayoutProperty('water_ocean', 'visibility', 'none');
               this.map.getStyle().layers!.forEach(layer2 => {
                  // tslint:disable-next-line
                  if (layer2["source-layer"] && layer2["source-layer"] === 'transportation') {
                     // tslint:disable-next-line
                     this.map.setLayoutProperty(layer2["id"], 'visibility', 'none');
                  }
               });
               break;
            }
            case 'OrthophotoNoWaterMask': {
               this.restoreNormalVisibility()
               this.map.setLayoutProperty('orthophoto', 'visibility', 'visible');
               this.map.setLayoutProperty('building', 'visibility', 'none');
               this.map.setLayoutProperty('building-3d', 'visibility', 'none');
               this.map.setLayoutProperty('water_ocean', 'visibility', 'none');
               this.map.setLayoutProperty('water_ocean_lowzoomonly', 'visibility', 'none');
               this.map.getStyle().layers!.forEach(layer2 => {
                  // tslint:disable-next-line
                  if (layer2["source-layer"] && layer2["source-layer"] === 'transportation') {
                     // tslint:disable-next-line
                     this.map.setLayoutProperty(layer2["id"], 'visibility', 'none');
                  }
               });
               break;
            }
            case 'Map': {
               this.restoreNormalVisibility();
               break;
            }
            case 'BlankMap': {
               let layer = Object.keys(this.mapLayoutRestore)
               layer.forEach((a) => {
                  this.map.setLayoutProperty(a, 'visibility', "none")
               })
               break;
            }
            default:
               throw new Error("Unexpected background layer type: " + layer);
         }
      }
   }

   public applyMapStyleMods(style: any[], mods?: string[]): any[] {
      let newStyle = style.map((layer) => {
         return mods?.reduce((acc, mod) => {
            return this.modLayer(acc, mod)
         }, layer) ?? layer;
      });
      return newStyle ?? [];
   }

   private modLayer(layer: any, mod: string) {
      switch (mod) {
         case "Ortophoto": {
            if (layer.id == "orthophoto") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            if (layer.id === "water_ocean_lowzoomonly") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            if (layer.id === "water_ocean") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer.id === "building-3d") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer["source-layer"] === "transportation") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "OrthophotoNoWaterMask": {
            if (layer.id == "orthophoto") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            if (layer.id === "water_ocean_lowzoomonly") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer.id === "water_ocean") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer.id === "building-3d") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            if (layer["source-layer"] === "transportation") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "No3D": {
            if (layer.type == "fill-extrusion") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoText": {
            if (layer.type == "symbol") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoRoads": {
            if (layer["source-layer"] === "transportation") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoBuildings": {
            if (layer["source-layer"] === "building") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "NoAeroways": {
            if (layer["source-layer"] === "aeroway") {
               layer["layout"] = { ...layer["layout"], visibility: "none" }
            }
            break;
         }
         case "HillShadows": {
            if (layer.id == "hillshade") {
               layer["layout"] = { ...layer["layout"], visibility: "visible" }
            }
            break;
         }
      }
      return layer
   }


   public mapStyleStyle(style?: string | object | undefined, mods?: string[]): object {
      if (style) {
         if (typeof style == "object") {
            return stylerSetGlyphProtocol({ ...style, layers: this.applyMapStyleMods(this.addPartSectionIfMissing(style["layers"]), mods ?? []) });
         } else if (style.startsWith("background-")) {
            let defaultStyle = getMapStyle()
            return stylerSetGlyphProtocol({ ...defaultStyle, layers: this.applyMapStyleMods(this.addPartSectionIfMissing([{ id: "background", type: "background", paint: { 'background-color': style.split("-").at(-1) ?? "white" } }]), mods ?? []) });
         } else {
            let styleRes = getMapStyle(style)
            return stylerSetGlyphProtocol({ ...styleRes, layers: this.applyMapStyleMods(this.addPartSectionIfMissing(styleRes["layers"]), mods ?? []) });
         }
      } else {
         let styleRes = getMapStyle()
         return stylerSetGlyphProtocol({ ...styleRes, layers: this.applyMapStyleMods(this.addPartSectionIfMissing(styleRes["layers"]), mods ?? []) });
      }
   }

   public async MapSource(builtInSources?: BuiltInVectorSources | string) {
      let sourceUrl = getVectorSource(builtInSources)
      if (typeof sourceUrl == "string" && sourceUrl.startsWith("http")) {
         let source = await fetch(sourceUrl);
         if (!source.ok) {
            console.error("Error loading vector source: " + source.statusText)
            return;
         }
         let sourceJson = await source.json();
         Object.keys(sourceJson).forEach((key) => {
            (this.map.getSource(key) as M.VectorTileSource).setUrl(sourceJson[key].url)
         })
      } else {
         let sourceJson = sourceUrl
         Object.keys(sourceJson).forEach((key) => {
            (this.map.getSource(key) as M.VectorTileSource).setUrl(sourceJson[key].url)
         })
      }
   }

   public async switchMapStyle(style: string | object, mods?: string[]) {
      if (typeof style == "object") {
         if (style["layers"]) {
            this.setMapStyleDiff(style, mods)
            return
         } else {
            this.setMapStyleDiff({ layers: style }, mods)
         }
         return
      }
      if (style.startsWith("http")) {
         await fetch(style).then((response) => {
            if (response.ok) {
               return response.json()
            } else {
               console.error("Error loading map style: " + response.statusText)
            }
         }).then((a) => {
            this.setMapStyleDiff(a, mods)
         })
         return
      }
      if (style.startsWith("background-")) {
         let backgroundStyle = {
            layers: [{ id: "background", type: "background", paint: { 'background-color': style.split("-").at(-1) ?? "white" } }]
         } as any;
         this.setMapStyleDiff(backgroundStyle, mods)
         return
      }
      let styleObj = getMapStyle(style)
      this.setMapStyleDiff(styleObj, mods)

   }

   private setMapStyleDiff(newStyle: any, mods?: string[]) {
      let newParts = this.splitLayersIntoParts(this.applyMapStyleMods(this.addPartSectionIfMissing(newStyle.layers), mods))
      let oldParts = this.splitLayersIntoParts(this.map.getStyle().layers!)

      let parts = [...new Set([...Object.keys(newParts), ...Object.keys(oldParts)])];
      let added: any[] = []
      parts.forEach((a) => {
         this.diffForPart(newParts[a] ?? [], oldParts[a] ?? [], added)
         added = [...added, ...newParts[a].map((a) => a.id) ?? []]
      })
   }

   private diffForPart(newLayers, oldLayers, notAlreadyAdded?: string[]) {
      let unusedReplacement = newLayers
      let lastLayerId = newLayers.at(-1)?.id ?? "";
      let wrongOrder = false;
      oldLayers.forEach((layer, idx) => {
         if (newLayers[idx] && newLayers[idx].id != layer.id) wrongOrder = true;
         let replaceMent = newLayers.find((b) => b.id == layer.id)
         if (replaceMent) {
            unusedReplacement = unusedReplacement.filter((b) => b.id !== layer.id)
            this.replacePaint(layer, replaceMent.paint)
            this.replaceLayout(layer, replaceMent.layout)
            this.replaceFilter(layer, replaceMent.filter)
            this.replaceMinMax(layer, replaceMent.minzoom, replaceMent.maxzoom)
         } else {
            if (notAlreadyAdded?.includes(layer.id)) {
               return
            }
            this.map.removeLayer(layer.id)
         }
      })
      if (lastLayerId) {
         unusedReplacement.forEach((layer) => {
            if (layer.id == lastLayerId) return
            if (this.map.getLayer(layer.id)) {
               this.map.removeLayer(layer.id)
            }
            this.map.addLayer(layer, lastLayerId);
         })
         if (wrongOrder) {
            let pre = newLayers.at(-1);
            newLayers.reverse().forEach((layer) => {
               if (layer.id != pre?.id) {
                  this.map.moveLayer(layer.id, pre?.id)
                  pre = layer
               }
            })
         }
      }
   }

   private addPartSectionIfMissing(layer: any[]) {
      let missingLayers = ["part-1-end", "part-2-start", "part-2-end"]
      if (layer[0].id !== "part-1-start") {
         layer.unshift(partLayer("part-1-start"))
      }
      layer.forEach((a) => {
         if (missingLayers.includes(a.id)) {
            missingLayers = missingLayers.filter((b) => b != a.id)
         }
      })
      missingLayers.forEach((a) => {
         layer.push(partLayer(a))
      })
      return layer
   }

   private splitLayersIntoParts(layers: any[]) {
      let startEndRegex = new RegExp(/^part-(\d+)-(?:start|end)$/)
      let currentPart: string = layers[0].id ?? "" //Handles styles that doen't use part-1-start/end
      let partToLayers: { [key: string]: any[] } = { [currentPart]: [] }
      layers.forEach((layer) => {
         if (layer.id.endsWith("-start")) {
            currentPart = startEndRegex.exec(layer.id)?.[0] ?? ""
            if (currentPart) {
               partToLayers[currentPart] = [layer]
               return
            }
         }
         if (currentPart) {
            partToLayers[currentPart].push(layer)
         }
         if (layer.id.endsWith("-end")) {
            currentPart = ""
         }
      })
      return partToLayers
   }

   public addAttribution(att: any) {
      // this.map.addControl(new M.AttributionControl({
      //    customAttribution: [ att ]
      // }));
      this.attributionList.push(att);
   }

   /**
    * @
    * @deprecated true
    * @returns 
    */
   public getMapPop() {
      //TODO: Create way to add popups without getMap()
      return this.map
   }

   // public toggleLayerVisibility(layerHandle:L.Layer):boolean {
   //    let map = this.getMap();
   //    let isVisibleAfter;
   //    if(map.hasLayer(layerHandle)) {
   //       map.removeLayer(layerHandle);
   //       isVisibleAfter = false;
   //   } else {
   //       map.addLayer(layerHandle);        
   //       isVisibleAfter = true;
   //   }
   //   return isVisibleAfter;
   // }

   public setLayerVisibility(layerHandle: MitLayerHandle, show: boolean) {
      layerHandle.hasLayers() && layerHandle.getLayerIds().forEach((layerId) => {
         this.map && this.map.setLayoutProperty(layerId, 'visibility', show ? 'visible' : 'none');
      });
      layerHandle.layersWithPieCharts.forEach((sourceId) => {
         if (show) {
            this.markersBySourceId[sourceId] = {}
         } else {
            Object.keys(this.markersBySourceId[sourceId] || {}).forEach((marker) => this.markersBySourceId[sourceId][marker].remove())
            delete this.markersBySourceId[sourceId]
         }
      })
   }

   public getLayerVisibility(layerHandle: MitLayerHandle): boolean {
      // todo: Implement
      let layerId = layerHandle.getLayerIds()[0];
      // todo: this always returns undefined.
      let result = this.map && this.map.getLayoutProperty(layerId, 'visibility');
      return result;
   }

   public setViewerState(newState: ViewerState) {
      // wait a bit to mitigate bug in Leaflet when changing view.
      let waitMilliSeconds = 500;
      setTimeout(
         () => {
            let map1 = newState && newState.center && this.map && this.map.panTo(MitLatLng.fromL(newState.center).getLngLat());
            setTimeout(
               () => {
                  newState && newState.zoomFactor && this.map && (map1 || this.map).setZoom(newState.zoomFactor);
               },
               waitMilliSeconds);
         },
         waitMilliSeconds);
   }

   public easeTo(options: any, eventData?: any): void {
      this.map?.easeTo?.(options, eventData);
   }

   public fire(event: any, eventData?: any): void {
      this.map?.fire?.(event, eventData);
   }

   public zoomToFeature(target: MitLayerHandle) {
      if (target.hasSource()) {
         this.zoomToData(target.getSource().data);
      } else {
         throw new Error("zoomToFeature: MitLayerHandleMaplibre has no source");
      }
   }

   public setData(layer: MitLayerHandle, geoJson: any): void {
      if (layer.hasSource()) {
         (this.map?.getSource(layer.sourceId) as M.GeoJSONSource | undefined)?.setData(geoJson)
      }
   }

   public zoomToData(data: any, padding?: number) {
      let boundingBox = turf.bbox(data);
      try {
         this.map && this.map.fitBounds(
            ((boundingBox.length === 4) ? boundingBox : boundingBox.splice(0, 4)) as [number, number, number, number],
            {
               // linear:true,
               padding: { top: padding ?? 25, bottom: padding ?? 25, left: padding ?? 25, right: padding ?? 25 },
               maxZoom: 19
            }
         );
      } catch (err: any) {
         const evv = new CustomEvent('mit-error-handler', { detail: err.message || err });
         document.body.dispatchEvent(evv);

         // appMessageDispatch(actionSetErrorMessage(Localization.getText("Cannot display cadaster information for this point")));
      }
   }

   public newPoiLayer(): MitLayerHandle {
      return new MitLayerHandleMaplibre();
   }

   public openPopup() {
      // let popup=L.popup({
      //    closeButton:false,
      //    closeOnClick:true,
      //    className:"mit-report-popup"
      // });
      // popup.setContent(val);
      // popup.setLatLng(latlng);
      // popup.openOn(this.map);
   }

   public getPoints(): MitLatLng[] {
      return [];
      // let result:MitLatLng[] = [];
      // this.map.eachLayer(function (layer:L.Layer) {
      //   // Lat, long of current point
      //   if (layer instanceof L.Marker) {
      //     let layer2:L.Marker = layer as L.Marker;
      //     result.push(layer2.getLatLng());
      //   } else {
      //     if (layer instanceof L.CircleMarker) {
      //       let layer3:L.CircleMarker = layer as L.CircleMarker;
      //       result.push(layer3.getLatLng());  
      //     }
      //   }
      // });
      // return result;
   }

   public remove() {
      this.map.remove();
   }

   on(event: string, handler: any): void;
   on(event: string, layerid: string, handler: any)
   on(event: string, layeHandler: any, handler?: any): void {
      if (handler) {
         this.map.on(event as keyof M.MapLayerEventType, layeHandler, handler);
         return
      }
      this.map.on(event, layeHandler)
   }

   off(event: string, handler: any): void;
   off(event: string, layerid: string, handler: any)
   off(event: string, layeHandler: any, handler?: any): void {
      if (handler) {
         this.map.off(event as keyof M.MapLayerEventType, layeHandler, handler);
         return
      }
      this.map.off(event, layeHandler)
   }

   once(event: string, handler: any): void;
   once(event: string, layerid: string, handler: any)
   once(event: string, layeHandler: any, handler?: any): void {
      if (handler) {
         this.map.once(event as keyof M.MapLayerEventType, layeHandler, handler);
         return
      }
      this.map.once(event, layeHandler)
   }

   getBounds() {
      return this.map.getBounds()
   }
   getBearing(): number {
      return this.map && this.map.getBearing() || 0;
   }
   getPitch(): number {
      return this.map && this.map.getPitch() || 0;
   }
   getCanvas() {
      return this.map.getCanvas()
   }
   queryRenderedFeatures(geometryOrOptions?, options?) {
      return this.map.queryRenderedFeatures(geometryOrOptions, options)
   }

   public setLayoutProperty(layer: MitLayerHandleMaplibre, property) {
      layer.getLayerIds().map((layerId) => {
         Object.keys(property).forEach((key) =>
            this.map.setLayoutProperty(layerId, key, property[key])
         )
      })
   }

   public setPaintProperty(layer: MitLayerHandleMaplibre, style) {
      layer.getLayerIds().map((layerId) => {
         Object.keys(style).forEach((key) =>
            this.map.setPaintProperty(layerId, key, style[key])
         )
      })
   }

   public hasImage(id: string) { return this.map.hasImage(id) }
   public loadImage(url: string) { return this.map.loadImage(url) }
   public addImage(id: string, image: HTMLImageElement | ImageBitmap | ImageData | { width: number; height: number; data: Uint8Array | Uint8ClampedArray; } | M.StyleImageInterface) { this.map.addImage(id, image) }

   public renderCatchments() {
      // if (this.catchment) { 
      //    this.catchment.clearLayers();
      // } else {
      //    this.catchment = L.layerGroup(undefined, {}).addTo(this.map);
      // }

      // // let polygons:any[] = polygons.features;
      // let jsonForOneFeature;
      // let largestBounds;
      // let ordered:any[]=[];
      // const opacity = 0.75;
      // polygons && polygons.forEach((itm) => {
      //     let time = itm.properties.time;
      //     let timeindex = travelTimes.findIndex((value)=> { return value === time; });
      //     ordered[timeindex]=itm;
      // });
      // let reversed = ordered.reverse();
      // reversed && reversed.forEach((item, idx) => {
      //     let time = item.properties.time;
      //     let timeindex = travelTimes.findIndex((value)=> { return value === time; });
      //     let color = colorScale[timeindex];
      //     let layer = L.geoJSON(undefined, {
      //         style: (feature:any) => {
      //             return {
      //                 fillColor : color,
      //                 fillOpacity : opacity,
      //                 color : color,
      //                 opacity: opacity,
      //                 stroke : true,
      //                 fill : true,
      //                 weight: 2
      //             };
      //         }
      //       }).addTo(this.catchment);
      //     jsonForOneFeature = {
      //       //   ...result, // this is to include crs and properties from the original data
      //         features: [item]
      //     };
      //     layer.addData(jsonForOneFeature);
      //     // The first layer is the largest
      //     if (!largestBounds) {
      //         largestBounds = layer.getBounds();
      //     }
      // });
      // this.map.fitBounds(largestBounds);
   }

   public hideCatchments() {
      if (this.catchment) {
         this.catchment.clearLayers(this);
      }
   }

   public getZoom() {
      return this.map && this.map.getZoom() || 0;
   }

   public getCenter() {
      return MitLatLng.fromM(this.map.getCenter());
   }

   public panTo(location: MitLatLng) {
      this.map && this.map.panTo(location.getLngLat());
   }

   public setZoom(zoom: number) {
      return this.map && this.map.setZoom(zoom);
   }

   public getLayers(): string[] {
      const layers = this.map.getStyle().layers;
      return layers.map(function (layer) {
         return layer.id;
      });
   }

   public removeLayer(layerHandle: MitLayerHandleMaplibre) {
      // remove layers first, then source
      layerHandle.getLayersConsumingEvents().forEach(layerId => {
         GenerateGeom.removeEventListeners(this.map, layerId);
      });
      layerHandle.hasLayers() && layerHandle.getLayerIds().forEach((layerId) => {
         this.map && this.map.getLayer(layerId) && this.map.removeLayer(layerId);
      });
      layerHandle.hasSource() && this.map.getSource(layerHandle.getSourceId()) && this.map.removeSource(layerHandle.getSourceId());
   }

   public addLayer(layerHandle: MitLayerHandleMaplibre)
   public addLayer(layerHandle: MitLayerHandleMaplibre, beforeLayerHandles: MitLayerHandleMaplibre[])
   public addLayer(layerHandle: MitLayerHandleMaplibre, beforeLayerHandles?: MitLayerHandleMaplibre[]) {
      if (!this.map.getSource(layerHandle.getSourceId()))
         layerHandle.hasSource() && this.map && this.map.addSource(layerHandle.getSourceId(), layerHandle.getSource());
      layerHandle.hasLayers() && layerHandle.getLayers().forEach((layer) => {
         let dataLayerIds = this.getLayers().filter((a) => a.includes("layer-id"));
         let layerID = layer.id
         let layerPriKey = PriotisedMapLayerTypes.findIndex((a) => layerID.includes(a))
         let beforeTheseTypes = [...PriotisedMapLayerTypes].splice(0, layerPriKey + 1).filter((a) => a !== PriotisedMapLayerTypes[layerPriKey])
         let beforeThis: string | undefined = undefined;

         let layers = beforeLayerHandles?.map((a) => {
            const acceptedLayers = a.getLayerIds().find((a) => {
               const hpriKey = PriotisedMapLayerTypes.findIndex((b) => a.includes(b))
               return layerPriKey == hpriKey
            })
            return acceptedLayers;
         })
         beforeThis = layers && layers.find((a) => a !== undefined);



         beforeThis ??= dataLayerIds.find((a) => beforeTheseTypes.some((before) => a.includes(before)))
         if (this.map.getLayer(layer.id)) {
            this.map.removeLayer(layer.id)
         }
         if (beforeThis) {
            this.map && this.map.addLayer(layer, beforeThis);
            return
         }
         this.map && this.map.addLayer(layer);
      });
      layerHandle.layersWithPieCharts.forEach((sourceId) => {
         if (this.markersBySourceId[sourceId]) {
            Object.keys(this.markersBySourceId[sourceId]).forEach((marker) => {
               // clear before removing refs
               this.markersBySourceId[sourceId][marker].remove()
            })
         }
         this.markersBySourceId[sourceId] = {}
      })
   }

   public updateLayer(layerHandle: MitLayerHandleMaplibre) {
      if (layerHandle.hasSource() && this.map) {
         if (this.map.getSource(layerHandle.getSourceId())) {
            (this.map as any).getSource(layerHandle.getSourceId() as any)!.setData(layerHandle.getSource().data);
         } else {
            this.map.addSource(layerHandle.getSourceId(), layerHandle.getSource());
         }
      }
      if (layerHandle.hasLayers() && this.map) {
         layerHandle.getLayers().forEach((layer) => {
            if (this.map.getLayer(layer.id)) {
               this.map.removeLayer(layer.id);
            }
            this.map.addLayer(layer);
         });
      }
   }

   public moveLayer(layerHandle: MitLayerHandleMaplibre) {
      // layerHandle.hasSource() && this.map && this.map.addSource(layerHandle.getSourceId(), layerHandle.getSource());
      layerHandle.hasLayers() && layerHandle.getLayers().forEach((layer) => {
         let dataLayerIds = this.getLayers().filter((a) => a.includes("layer-id"));
         let layerID = layer.id
         let layerPriKey = PriotisedMapLayerTypes.findIndex((a) => layerID.includes(a))
         let beforeTheseTypes = [...PriotisedMapLayerTypes].splice(0, layerPriKey + 1).filter((a) => a !== PriotisedMapLayerTypes[layerPriKey])
         let beforeThis: string | undefined = undefined;

         beforeThis ??= dataLayerIds.find((a) => beforeTheseTypes.some((before) => a.includes(before)))
         if (beforeThis) {
            this.map && this.map.moveLayer(layerID, beforeThis);
            return
         }
         this.map && this.map.moveLayer(layerID);
      });
   }

   public zoomToPoint(latlng: MitLatLng) {
      // let p:MitLatLngExpression = point;
      // let bounds: MitLatLngBoundsExpression = MitLatLngBounds(p, p);
      // // zoom to the area
      // this.map.fitBounds(bounds);
      this.map && this.map.panTo(latlng.getLngLat());
   }

   public createCircle(): MitLayerHandle {
      // let locationMarkers = new L.FeatureGroup();
      // let circle = L.circle(latlng, radius);
      // circle.addTo(locationMarkers);
      // locationMarkers.addTo(this.map);
      // return locationMarkers;
      return new MitLayerHandleMaplibre();
   }

   public removeLayerByHandle(handle: MitLayerHandle) {
      this.removeLayer(handle);
   }

   public locate() {
      // this.map!.locate({setView: false, watch: true, maxZoom: 16});
   }

   public initializeBackgroundLayers() {
      // todo: implement
   }

   private createWMSSource(fl: FeatureLayer) {
      return {
         'type': 'raster',
         // use the tiles option to specify a WMS tile source URL
         'tiles': [
            fl.getTileURL()
            // Nolonger works 'https://services.datafordeler.dk/Matrikel/MatrikelGaeldendeOgForeloebigWMS/1.0.0/WMS?' +
            // 'bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.3.0&request=GetMap&transparent=TRUE&crs=EPSG:3857&width=256&height=256' +
            // '&layers=Jordstykke_Gaeldende&styles=Jordstykke_Gaeldende_Roed&username=QBXRBEJZGQ&password=Ballerup2100$'
         ],
         'tileSize': 256,
         'maxzoom': fl.layer.maxZoom,
         'minzoom': fl.layer.minZoom,
      }
   }

   private createWMSLayer(id: string, source: string, fl: FeatureLayer) {
      return {
         'id': id,
         'type': 'raster',
         'source': source,
         'paint': fl.layer.paint || {},
         'maxzoom': fl.layer.maxZoom,
         'minzoom': fl.layer.minZoom,
      }
   }

   public addVectorLayer(layer: VectorLayer | GeojsonWFS): MitLayerHandle {
      const sourceSpec = layer.getSource();
      const layerSpec = layer.getLayers();
      if (this.isInitialized()) {
         if (!this.map.getSource(layer.getSourceID()))
            this.map.addSource(layer.getSourceID(), sourceSpec as any)
         let beforeThisLayer = this.map.getStyle().layers.map((a) => a.id).find((a) => a.startsWith("layer-id"))
         layerSpec.map((a, idx) => {
            if (!this.map.getLayer(layer.getLayerID(idx)))
               this.map.addLayer(a as any, beforeThisLayer);
         })
      }
      const handle = new MitLayerHandleMaplibre();
      handle.addSource(layer.getSourceID(), layer.getSource())
      if (layer instanceof GeojsonWFS) {
         let bounds = this.map.getBounds();
         let zoom = this.map.getZoom();
         if (layer.layer.minZoom < zoom) {
            this.loadingMoveEnd = true
            fetch(layer.getDataSrc(bounds)).then(async (data) => {
               let json
               if (data.headers.get("Content-Type")?.includes("text/xml")) {
                  json = WFStoGeoJson(await data.text());
               } else if (
                  data.headers.get("Content-Type")?.includes("application/json") &&
                  data.headers.get("Content-Type")?.includes("ISO-8859-1")
               ) {
                  let decoder = new TextDecoder("ISO-8859-1")
                  json = await JSON.parse(decoder.decode(await data.arrayBuffer()))
               } else {
                  json = await data.json()
               }
               (this.map.getSource(layer.getSourceID()) as M.GeoJSONSource | undefined)?.setData(
                  await (layer.getDataTransformer())(json, { zoom: zoom, bounds: bounds })
               )
               this.loadingMoveEnd = false
            })
         }
         this.moveendFeatureLayer.push({
            sourceID: layer.getSourceID(),
            minZoom: layer.layer.minZoom,
            dataGetter: async (bounds) => {
               try {
                  let data = (await fetch(layer.getDataSrc(bounds)))
                  if (data.headers.get("Content-Type")?.includes("text/xml")) {
                     return WFStoGeoJson(await data.text());
                  } else if (data.headers.get("Content-Type")?.includes("application/json")) {
                     if (data.headers.get("Content-Type")?.includes("ISO-8859-1")) {
                        let decoder = new TextDecoder("ISO-8859-1")
                        return await JSON.parse(decoder.decode(await data.arrayBuffer()))
                     }
                     return await data.json()
                  } else {
                     throw new Error("Unknown content type")
                  }
               } catch (err) {
                  console.error(err)
                  let message = ErrorByTileSource(layer.getDataSrc(bounds))
                  document.getElementById("NonBlockingError")?.dispatchEvent(new CustomEvent("NonBlockingError", { "detail": message }))
               }
               return turf.featureCollection([])
            },
            dataTransformer: layer.getDataTransformer()
         })
      }
      layerSpec.map((_a, idx) => {
         handle.addLayer(layer.getLayerID(idx));
      })
      return handle
   }

   public addFeatureLayer(key: string, fl: FeatureLayer): MitLayerHandle {
      let layerId = this.constructBackgroundLayerId(key);
      let sourceId = this.constructBackgroundLayerSourceId(key);
      let sourceSpec = this.createWMSSource(fl);
      let layerSpec = this.createWMSLayer(layerId, sourceId, fl);

      let before = fl.layer.before
      if (this.isInitialized()) {
         if (!this.map.getSource(sourceId))
            this.map.addSource(sourceId, sourceSpec as any);
         let beforeThisLayer = this.map.getStyle().layers.map((a) => a.id).find((a) => a.startsWith("layer-id") || (before && a == before))
         if (!this.map.getLayer(layerId))
            this.map.addLayer(layerSpec as any, beforeThisLayer);
      }

      let handle = new MitLayerHandleMaplibre();
      handle.addSource(sourceId, sourceSpec);
      handle.addLayer(layerId);


      return handle;
   }


   public searchKey<T extends T[] | string>(key, nestedArray: T): boolean {
      if (Array.isArray(nestedArray)) {
         for (let i = 0; i < nestedArray.length; i++) {
            if (this.searchKey(key, nestedArray[i])) {
               return true;
            }
         }
      } else {
         if (nestedArray === ("@" + key)) {
            return true;
         }
      }
      return false;
   }


   public updateVectorLayer(layer: VectorLayer | GeojsonWFS, value: { [key: string]: any }): MitLayerHandle {
      const keys = Object.keys(value)
      let layerSpec = layer.getLayers()
      //TODO: Find what needs be changed
      for (let i = 0; i < layerSpec.length; i++) {
         let preTrans = layer.layer.layers[i] as any
         // filter
         if ("filter" in preTrans && keys.some((key) => this.searchKey(key, preTrans.filter))) {
            this.map.setFilter(layer.getLayerID(i), layer.getLayers()[i].filter, { validate: true })
         }
         // paint
         let paintKeys = preTrans.paint && Object.keys(preTrans.paint) || []
         for (let j = 0; j < paintKeys.length; j++) {
            if (keys.some((key) => this.searchKey(key, preTrans.paint[paintKeys[j]]))) {
               this.map.setPaintProperty(layer.getLayerID(i), paintKeys[j], layer.getLayers()[i].paint[paintKeys[j]])
            }
         }
         // layout
         let layoutKeys = preTrans.layout && Object.keys(preTrans.layout) || []
         for (let j = 0; j < layoutKeys.length; j++) {
            if (keys.some((key) => this.searchKey(key, preTrans.layout[layoutKeys[j]]))) {
               this.map.setLayoutProperty(layer.getLayerID(i), layoutKeys[j], layer.getLayers()[i].layout[layoutKeys[j]])
            }
         }
      }

      let handle = new MitLayerHandleMaplibre();
      handle.addSource(layer.getSourceID(), layer.getSource());
      layerSpec.map((_a, idx) => {
         handle.addLayer(layer.getLayerID(idx));
      })
      return handle;
   }

   public updateFeatureLayer(key: string, fl: FeatureLayer): MitLayerHandle {
      let layerId = this.constructBackgroundLayerId(key);
      let sourceId = this.constructBackgroundLayerSourceId(key);
      let sourceSpec = this.createWMSSource(fl);
      let layerSpec = this.createWMSLayer(layerId, sourceId, fl);
      if (this.isInitialized()) {
         if (this.map.getSource(sourceId)) {
            Object.keys(layerSpec.paint || {}).forEach((a) => {
               this.map.setPaintProperty(layerId, a, layerSpec.paint[a])
            })
         }
      }
      let handle = new MitLayerHandleMaplibre();
      handle.addSource(sourceId, sourceSpec);
      handle.addLayer(layerId);


      return handle;
   }

   constructBackgroundLayerId(key: string) {
      return "backgroundlayer" + key;
   }
   constructBackgroundLayerSourceId(key: string) {
      return "backgroundlayerSource" + key;
   }

   public changeVectorVisibility(layer: VectorLayer, visibility: "none" | "visible") {
      layer.getLayerID().map((a) => {
         this.map && this.map.getLayer(a) && this.map.setLayoutProperty(a, 'visibility', visibility)
      })
   }

   public changeDynamicVisibility(layer: DynamicDataDrivenLayer, visibility: "none" | "visible") {
      let layerID = "data-layer-" + layer.layer.label;
      this.map && this.map.getLayer(layerID) && this.map.setLayoutProperty(layerID, 'visibility', visibility)
   }

   public updateDynamicLayer(_key, layer: DynamicDataDrivenLayer, value) {
      if (layer instanceof DynamicDataDrivenLayerPropertyInfo) {
         let textSpec = layer.createTextFieldSpec(value);
         let layerID = "data-layer-" + layer.layer.label;
         this.map.setLayoutProperty(layerID, 'text-field', textSpec);
      }
   }

   public addDynamicLayer(layer: DynamicDataDrivenLayer) {
      let layerID = "data-layer-" + layer.layer.label;
      let sourceID = "data-source-" + layer.layer.label;

      (!this.map.getSource(sourceID)) && this.map.addSource(sourceID, layer.createGeoJsonSource() as any);
      (!this.map.getLayer(layerID)) && this.map.addLayer(layer.createLayer(sourceID, layerID) as any);
      this.updateDynamicLayer(layerID, layer, layer.layer.variantValues);
      let bounds = this.map.getBounds();
      let zoom = this.map.getZoom();
      if (layer.layer.minZoom < zoom) {
         this.loadingMoveEnd = true
         layer.getDataForBBOX(bounds.toArray().flat()).then((json) => {
            (this.map.getSource(sourceID) as M.GeoJSONSource | undefined)?.setData(
               json
            )
            this.loadingMoveEnd = false
         })
      }

      this.moveendFeatureLayer.push({
         sourceID: sourceID,
         minZoom: layer.layer.minZoom,
         dataGetter: async (bounds) => {
            return await layer.getDataForBBOX(bounds.toArray().flat())
         },
         dataTransformer: async (a) => { return a }
      })

   }

   public removeDynamicLayer(layer: DynamicDataDrivenLayer) {
      let layerID = "data-layer-" + layer.layer.label;
      let sourceID = "data-source-" + layer.layer.label

      this.map && this.map.getLayer(layerID) && this.map.removeLayer(layerID)
      this.map && this.map.getSource(sourceID) && this.map.removeSource(sourceID)
      let x = this.moveendFeatureLayer.findIndex((a) => a.sourceID == sourceID)
      if (x !== -1) {
         this.moveendFeatureLayer.splice(x, 1)
      }
   }

   public removeVectorLayer(layer: VectorLayer | GeojsonWFS) {
      layer.getLayerID().map((a) => {
         this.map && this.map.getLayer(a) && this.map.removeLayer(a)
      })
      this.map && this.map.getSource(layer.getSourceID()) && this.map.removeSource(layer.getSourceID())
      if (layer instanceof GeojsonWFS) {
         let x = this.moveendFeatureLayer.findIndex((a) => a.sourceID == layer.getSourceID())
         if (x !== -1) {
            this.moveendFeatureLayer.splice(x, 1)
         }
      }
   }

   public changeFeatureVisibility(key: string, visibility: "none" | "visible") {
      let a = this.constructBackgroundLayerId(key);
      this.map && this.map.getLayer(a) && this.map.setLayoutProperty(a, 'visibility', visibility)
   }

   public removeFeatureLayer(key: string) {
      try {
         let layerId = this.constructBackgroundLayerId(key);
         let sourceId = this.constructBackgroundLayerSourceId(key);
         // Todo: Mapbox does not support event listeners on raster layers. Need a general click handler and control it from there.
         // GenerateGeomMapbox.removeEventListenersForFeatureLayer(this.map, layerId);
         this.map && this.map.getLayer(layerId) && this.map.removeLayer(layerId);

         let src = this.map.getSource(sourceId) as M.RasterTileSource;
         let urlIdent = urlIdentFromUrl(src?.tiles?.[0])
         if (urlIdent) {
            this.banList = this.banList.filter((a) => a !== urlIdent);
         }

         this.map && this.map.getSource(sourceId) && this.map.removeSource(sourceId);

      } catch (error) {
         throw new Error(`Could not remove feature layer ${key}: ${error}`);
      }
   }

   public getLatLngFromMouseEvent(clickEvent: any): MitLatLng {
      let e = clickEvent as M.MapMouseEvent;
      return new MitLatLng(e.lngLat.lat, e.lngLat.lng);
   }

   public showUserLocation(show: boolean): void {

      function createPointSource(lat: number, lng: number) {
         return {
            type: "geojson",
            data: {
               "type": "FeatureCollection",
               "features": [circle([lng, lat], 25, { units: "meters" })]
            }
         };
      }

      function createPointLayer(id: string, source: string) {

         return {
            id: id,
            type: 'fill',
            source: source,
            'layout': {
            },
            paint: {
               'fill-color': '#BD9060',
               'fill-opacity': 0.4,
               'fill-outline-color': '#FFFFFF'
            },
         };
      }

      const layer = "userLocationLayer";
      const source = "userLocationSource";
      let errorMsg;
      function getLocation(_map: M.Map, callback: (position) => void) {
         if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(callback, showError);
         } else {
            errorMsg = "Geolocation is not supported by this browser.";
            alert(errorMsg);
         }
      }

      function showPosition(map: M.Map, position: any, obj: MapFacadeMaplibre) {
         errorMsg = "Latitude: " + position.coords.latitude +
            "<br>Longitude: " + position.coords.longitude;
         // alert(errorMsg);
         let pointData = createPointSource(position.coords.latitude, position.coords.longitude);
         if (map.getSource(source)) {
            (map as any).getSource(source).setData(pointData.data);
         } else {
            map.addSource(source, pointData as any);
         }
         if (!map.getLayer(layer)) {
            map.addLayer(createPointLayer(layer, source) as any);
         }
         obj.zoomToData(pointData.data);
      }

      function showError(error) {
         switch (error.code) {
            case error.PERMISSION_DENIED:
               errorMsg = "User denied the request for Geolocation."
               alert(errorMsg);
               break;
            case error.POSITION_UNAVAILABLE:
               errorMsg = "Location information is unavailable."
               alert(errorMsg);
               break;
            case error.TIMEOUT:
               errorMsg = "The request to get user location timed out."
               alert(errorMsg);
               break;
            case error.UNKNOWN_ERROR:
               errorMsg = "An unknown error occurred."
               alert(errorMsg);
               break;
         }
      }
      if (show) {
         this.map && getLocation(this.map, (position) => { showPosition(this.map, position, this) });
      } else {
         // alert("switch off");
         if (this.map && this.map.getLayer && this.map.getLayer(layer)) {
            this.map.removeLayer(layer);
         }
      }
   }

   public replacePaint(a, b) {
      let props = new Set([...Object.keys(a.paint || {}), ...Object.keys(b || {})]);
      props.forEach((prop) => {
         if (b?.[prop] !== undefined) {
            this.map.setPaintProperty(a.id, prop, b[prop]);
         } else {
            this.map.setPaintProperty(a.id, prop, undefined);
         }
      })
   }

   public replaceLayout(a, b) {
      let props = new Set([...Object.keys(a.layout || {}), ...Object.keys(b || {})]);
      props.forEach((prop) => {
         if (b?.[prop] !== undefined) {
            this.map.setLayoutProperty(a.id, prop, b[prop]);
         } else {
            this.map.setLayoutProperty(a.id, prop, undefined);
         }
      })
   }

   public replaceFilter(a, b?) {
      if (b !== undefined) {
         this.map.setFilter(a.id, b);
      } else {
         this.map.setFilter(a.id, undefined);
      }
   }

   public replaceMinMax(a, b?, c?) {
      this.map.setLayerZoomRange(a.id, b, c);
   }
}

export class CircleSelector implements CircleSelectorAbstract {
   map: M.Map;
   callbackOnComplete: (latlng: MitLatLng, distance: number) => void;
   callbackOnMove: (latlng: MitLatLng, distance: number) => void;
   callbackOnCancel: () => void;
   DefaultDistance: number;
   MinimumDistance: number;
   MaximumDistance: number;
   // State:{
   selectionState: SelectionState = SelectionState.Disabled;
   marker: M.Marker | undefined;
   coords: M.LngLat | undefined;
   distance: number | undefined;
   // }
   layer = "circleselector";


   constructor(
      map: M.Map,
      callbackOnComplete: (latlng: MitLatLng, distance: number) => void,
      callbackOnMove: (latlng: MitLatLng, distance: number) => void,
      callbackOnCancel: () => void,
      DefaultDistance: number,
      MinimumDistance: number,
      MaximumDistance: number) {

      this.map = map;
      this.callbackOnComplete = callbackOnComplete;
      this.callbackOnMove = callbackOnMove;
      this.callbackOnCancel = callbackOnCancel;
      this.DefaultDistance = DefaultDistance;
      this.MinimumDistance = MinimumDistance;
      this.MaximumDistance = MaximumDistance;

   }

   public enable() {
      this.selectionState = SelectionState.ClickToSelectCenter;
      this.map.on("click", this.layer, this.mapClickHandler);
   }

   public disable() {
      this.cleanup();
   }

   public isDisabled() {
      return this.selectionState === SelectionState.Disabled;
   }

   public cleanup() {
      this.map && this.marker && this.marker.remove();
      this.map && this.map.off("click", this.layer, this.mapClickHandler);
      this.map && this.map.off("mousemove", this.layer, this.mapMoveHandler);
      // this.map && this.map.closePopup();
      this.selectionState = SelectionState.Disabled;
   }

   public remove() {
      this.map && this.marker && this.marker.remove();
   }

   public show(_latlng?: MitLatLng, _radius?: number) {
      // this.setOrUpdateMarker(latlng, radius);
   }

   public hide() {
      this.remove();
   }

   public getLatLng(): MitLatLng | undefined {
      return this.coords && MitLatLng.fromM(this.coords);
   }



   mapMoveHandler(e2: M.MapMouseEvent) {
      // find the distance between the center and mouse position.
      let f1 = this.coordToTurfFeature(e2.lngLat);
      let f2 = this.coordToTurfFeature(this.marker!.getLngLat());
      let dist = turf.distance(f1, f2, { units: 'kilometers' });
      dist = dist / 1000; // to meters.
      dist = 10 * (Math.floor(dist / 10)); // round to whole 10 meters
      if (!dist) { dist = 1; } // minimum distance of 1 m (otherwise we get error on missing param from servrer)
      if (dist < this.MinimumDistance) { dist = this.MinimumDistance; } // minimum distance of 50 m (otherwise we get error on missing param from servrer)
      if (dist > this.MaximumDistance) { dist = this.MaximumDistance; } // maximum distance of 10000 m (otherwise we get error on missing param from servrer)
      this.setOrUpdateMarker(undefined, dist);
      // ToDo: migrate to Maplibre popup.
      // var popup=L.popup({
      //     closeButton:false,
      //     closeOnClick:true,
      //     className:"mit-report-popup"
      //   });
      // popup.setContent(""+dist.toFixed(0)+"m");
      // popup.setLatLng(e2.latlng);
      // popup.openOn(this.map!);
      this.distance = dist;
      // todo: implement
      // this.callbackOnMove(this.marker!.getLngLat(), dist);
   }

   setOrUpdateMarker(_latlng?: MitLatLng, _radius?: number) {
      // ToDo: migrate to Maplibre.
      // if(this.marker) {
      //     latlng && this.marker.setLatLng(latlng);
      //     radius && this.marker.setRadius(radius);
      // } else {
      //     console.assert(latlng, "Event object expected");
      //     console.assert(radius, "Distance expected");
      //     var marker = L.circle(
      //         latlng!, 
      //         {
      //         radius: radius || 0,
      //         color: 'red'
      //         });
      //     marker.addTo(this.map);
      //     this.marker=marker;
      //     this.coords=latlng!;    
      // }
   }

   mapClickHandler(_e2: any) {
      // ToDo: migrate to Maplibre popup.
      //    switch(this.selectionState) {
      //        case SelectionState.ClickToSelectCenter:
      //            this.setOrUpdateMarker(e2.latlng, this.distance);
      //            this.map.on("mousemove", this.mapMoveHandler, this);
      //            this.selectionState = SelectionState.CenterSelected;
      //            this.callbackOnMove(this.coords, this.distance);
      //            break;
      //        case SelectionState.CenterSelected:
      //            this.map.off("mousemove", this.mapMoveHandler, this);
      //            this.selectionState = SelectionState.RimSelected;
      //            this.callbackOnComplete(this.coords, this.distance);
      //            break;
      //        case SelectionState.RimSelected:
      //            this.remove();
      //            this.selectionState = SelectionState.ClickToSelectCenter;
      //            this.marker = undefined;
      //            this.distance = this.DefaultDistance;
      //            break;
      //        default:
      //            throw Utils.createErrorEventObject("Unknown or unexpected state:"+this.selectionState);
      //    }
   }

   private coordToTurfFeature(coords: M.LngLat) {
      // var geometry:any = {
      //    "type": "Point",
      //    "coordinates": [coords.lng, coords.lat]
      //  };
      //  var feature = turf.feature(geometry);
      var feature = turf.point([coords.lng, coords.lat]);
      return feature;
   }
}

function ErrorMessageMapfacade(source: M.Source) {
   if ("tiles" in source && Array.isArray(source.tiles)) {
      return ErrorByTileSource(source.tiles[0])
   }
   return "Unknown error"
}

function ErrorByTileSource(a: string) {
   const errors = {
      "wms.cowi.com": "Cowi",
      "services.hxgncontent.com": "Hexagon",
      "lb.viamap.net": "Viamap-Geoserver",
      "api.dataforsyningen.dk": "Dataforsyningen",
      "services.datafordeler.dk": "Datafordeler",
      "geofa.geodanmark.dk": "Geodanmark",
      "gisp.bpst.dk": "Social- og Boligstyrelsen",
      "geoserver.plandata.dk": "Plandata",
      "arealeditering-dist-geo.miljoeportal.dk": "Miljøportal",
      "geocloud.vd.dk": "Vejdirektoratet",
      "www.kulturarv.dk": "Kulturarv",
      "wfs2-miljoegis.mim.dk": "Miljøgis",
      "data.geus.dk": "Geus",
      "vmgeoserver.vd.dk": "Vejdirektoratet",
      "b0501-prod-arldgeo-app.azurewebsites.net": "Areal Geo",
      "geodata.fvm.dk": "Landbrugs- og Fiskeristyrelsen",
      "gis.nst.dk": "Naturstyrelsen",
      "miljoegis.mim.dk": "Miljøgis",
      "miljoegis3.mim.dk": "Miljøgis",
      "arld-extgeo.miljoeportal.dk": "Miljøportal",
      "naturarealdatageo.miljoeportal.dk": "Miljøportal"
   }
   let errSource = Object.keys(errors).find((b) => a.includes(b))
   if (errSource) {
      return errors[errSource]
   }
   return "Unknown error"
}

var btdata: Uint8Array;
// oxlint-disable-next-line no-unused-vars
function blankTileData() {
   if (!btdata) {
      btdata = Uint8Array.from(atob("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"), (e) => e.charCodeAt(0));
   }
   return btdata
}

function urlIdentFromUrl(url: string | undefined) {
   if (!url) {
      return ""
   }
   let urlObj = new URL(url);
   return urlObj.hostname + "|" + urlObj.searchParams.get("LAYERS")
}

async function generateTileDataWithText(error?: string, x = 0.5, y = 0.5) {
   let canvas = document.createElement('canvas');
   canvas.width = 256;
   canvas.height = 256;
   let ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
   ctx.fillStyle = 'red';
   ctx.font = '12px Arial';

   let measure = ctx.measureText(error || "Error:");
   measure.width

   ctx.fillText(error || "Error:", (256 - measure.width) * x, (256 - measure.actualBoundingBoxDescent) * y);
   return createImageBitmap(ctx.getImageData(0, 0, 256, 256));
}

function partLayer(id: string) {
   return {
      "id": id,
      "type": "background",
      "layout": { "visibility": "none" }
   }
}